// Centralized service image map — real TNT Roofing project photographs.
//
// RULES FOR MAINTAINING THIS FILE:
//  1. Every entry must be a real TNT Roofing project photograph. No stock
//     photography and no AI-generated imagery.
//  2. No two entries may share the same image. Several components render these
//     maps side by side, so a repeated URL shows up as two identical pictures
//     in one row of cards. Uniqueness is what prevents that.
//  3. alt text must describe what the photograph ACTUALLY shows. Cards whose
//     subject is a small component (pipe boot, flashing, valley) show genuine
//     TNT roof photos rather than a stock close-up of that component, so the
//     alt describes the real project photo, never the component.
//
// Photo provenance: the TNT project gallery and the Project records in the
// database. Each caption below matches the documented project title/location.

const M = 'https://media.base44.com/images/public/69e98925de85ac022838e4c7/';

export const serviceImages = {
  // ── Roof Repair: "What TNT Repairs" cards ──
  pipeBootReplacement: {
    image: `${M}5620acaf1_9a904a4a4_IMG_9403-opt.webp`,
    alt: 'Real TNT Roofing project photo: close-up of architectural shingles being installed on a Northern Kentucky home',
  },
  flashingRepair: {
    image: `${M}b713738d8_fd3070bb1_IMG_7562-opt.webp`,
    alt: 'Real TNT Roofing project photo: sealant being applied with a caulking gun during a shingle roof repair',
  },
  damagedShingles: {
    image: `${M}4102e0035_707e68447_IMG_9374.webp`,
    alt: 'Real TNT Roofing project photo: storm-damaged roof with missing and lifted shingles before replacement',
  },
  ridgeCapValleyRepair: {
    image: `${M}4c9b30cfa_a1d17e920_f5b3bb084_IMG_9378-opt.webp`,
    alt: 'Real TNT Roofing project photo: aerial close-up of a completed architectural shingle roof system',
  },
  skylightFlashing: {
    image: `${M}6d09177b5_6720056d3_IMG_9380-opt.webp`,
    alt: 'Real TNT Roofing project photo: roof installation in progress on a large brick home in Northern Kentucky',
  },
  soffitFasciaGutterApron: {
    image: `${M}6badca339_1eba6fa2a_IMG_8739-opt.webp`,
    alt: 'Real TNT Roofing project photo: roof eaves and fascia visible below a standing seam metal roof in Union, KY',
  },

  // ── Main services: ServicesGrid cards ──
  roofReplacement: {
    image: `${M}766d2b84c_86192c2cd_163dbb9d4_IMG_3423-opt.webp`,
    alt: 'Real TNT Roofing project photo: roof replacement in progress with dump trailer and IKO Dynasty shingle pallets on site',
  },
  roofRepair: {
    image: `${M}59d131a6e_550e92e14_IMG_9402-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed shingle roof system on a Northern Kentucky home',
  },
  stormDamage: {
    image: `${M}800d1bd35_628b7d7cf_IMG_9390.webp`,
    alt: 'Real TNT Roofing project photo: storm-damaged townhome roof before replacement',
  },
  commercialRoofing: {
    image: `${M}6c836bf96_8924c6c97_IMG_2544-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed commercial TPO membrane roof on a dentist office in Alexandria, KY',
  },
  gutterInstallation: {
    image: `${M}1a48a33c1_a95edbf9c_4c5c502e4_29007-opt.webp`,
    alt: 'Real TNT Roofing project photo: seamless white gutter and downspout installation on a brick home',
  },
  sidingInstallation: {
    image: `${M}989e47004_27477ba7f_6452ff356_aftersidingpic-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed vinyl siding installation on a residential home with white trim',
  },

  // ── Additional service categories (centralized mapping per service type) ──
  tpoFlatRoofing: {
    image: `${M}daf23a579_957eda7a5_image.webp`,
    alt: 'Real TNT Roofing project photo: completed TPO membrane roof on the Take 5 Oil Change in Alexandria, KY',
  },
  epdmRoofing: {
    image: `${M}584d64c48_6b8cad86a_attVTdUwDrBmso1UwaXPyyTr11vswOSxH9zXE2zerNryNs-opt.webp`,
    alt: 'Real TNT Roofing project photo: existing ballasted flat roof before TPO replacement at Take 5 Oil Change in Alexandria, KY',
  },
  metalRoofing: {
    image: `${M}11cd0856b_46214048e_IMG_8744-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed 24-gauge standing seam metal roof on a home in Union, KY',
  },
  standingSeamMetalRoofing: {
    image: `${M}9e027500e_a32a693c6_IMG_8743-opt.webp`,
    alt: 'Real TNT Roofing project photo: standing seam metal roof panel detail, Union, KY',
  },
  emergencyTarping: {
    image: `${M}82b82fb99_b8940a348_IMG_9416.webp`,
    alt: 'Real TNT Roofing project photo: emergency roof tarping during a 24/7 storm response',
  },
  hailDamage: {
    image: `${M}1fcd38a99_e2beeebc8_IMG_2435-opt.webp`,
    alt: 'Real TNT Roofing project photo: hail-damaged roof documented for an insurance claim in Covington, KY',
  },
  windDamage: {
    image: `${M}b3c93d729_47f7f2159_IMG_2433-opt.webp`,
    alt: 'Real TNT Roofing project photo: storm-damaged roof in Florence, KY before replacement',
  },
  roofInspection: {
    image: `${M}150d46ee3_9b8a218cc_IMG_9400-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed roof replacement on a large Northern Kentucky home',
  },
  roofCoating: {
    image: `${M}a72adac13_3c2c0496a_IMG_4270-opt.webp`,
    alt: 'Real TNT Roofing project photo: commercial flat roof with satellite dish in Northern Kentucky',
  },
  gutterGuards: {
    image: `${M}5b6554d16_82bf29c3d_b34086c55_IMG_3072-opt.webp`,
    alt: 'Real TNT Roofing project photo: gutter protection installed along a residential roof edge',
  },
  sidingRepair: {
    image: `${M}95990224b_d990f3f9a_IMG_6878-opt.webp`,
    alt: 'Real TNT Roofing project photo: vinyl siding installation in progress with Norandex siding',
  },
  chimneyFlashingRepair: {
    image: `${M}797133b3a_270c8de36_IMG_9401-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed roof replacement on a brick home in Northern Kentucky',
  },
  ridgeVent: {
    image: `${M}ede6bb8fb_f1b9d4462_IMG_9398-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed roof on a ranch style home in Northern Kentucky',
  },
  skylightRepair: {
    image: `${M}39c743c80_6c28b26ef_IMG_9397-opt.webp`,
    alt: 'Real TNT Roofing project photo: new dark architectural shingles on a brick Cape Cod home',
  },
  barnRoofing: {
    image: `${M}18fd7c9f1_f21c84bed_IMG_9377-opt.webp`,
    alt: 'Real TNT Roofing project photo: aerial view of a completed roof and outbuilding',
  },
  roofVentilation: {
    image: `${M}9e036ae1c_b67e1e435_IMG_9396-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed roof replacement on a brick ranch home',
  },
  valleyRepair: {
    image: `${M}6f0c32d42_c82ba6810_IMG_9388-opt.webp`,
    alt: 'Real TNT Roofing project photo: storm-damaged brick colonial roof before replacement',
  },

  // ── Flashing types: FlashingTypesGrid cards ──
  chimneyFlashing: {
    image: `${M}12150e236_960638134_IMG_9385-opt.webp`,
    alt: 'Real TNT Roofing project photo: storm-damaged split-level brick roof before replacement',
  },
  wallHeadwallFlashing: {
    image: `${M}cf7fd8f7e_12473a9a6_IMG_9381-opt.webp`,
    alt: 'Real TNT Roofing project photo: storm-damaged country ranch roof before replacement',
  },
  valleyFlashing: {
    image: `${M}a423af3a6_9bce76496_IMG_9374.webp`,
    alt: 'Real TNT Roofing project photo: aged roof before architectural shingle replacement',
  },
  skylightCurbFlashing: {
    image: `${M}0aa8cddb6_b4d2e306b_IMG_9393.webp`,
    alt: 'Real TNT Roofing project photo: brick home with a new architectural shingle roof',
  },
  pipeBootFlashing: {
    image: `${M}90daaff93_2c782856c_IMG_9392.webp`,
    alt: 'Real TNT Roofing project photo: aerial view of a completed roof replacement',
  },
  dripEdgeGutterApron: {
    image: `${M}84ddcded8_e3e130f0c_ab4e9b730_gutterapronanddripedge-opt.webp`,
    alt: 'Real TNT Roofing project photo: gutter apron and drip edge along a residential roof edge',
  },
  gutterDebrisReal: {
    image: `${M}633d377cd_92b7d56b0_IMG_8230-opt.webp`,
    alt: 'Real TNT Roofing project photo: seamless aluminum gutter installation along a residential roof edge',
  },

  // ── CityPage Service Categories ──
  roofingServicesCategory: {
    image: `${M}5a8d650ad_tnt-city-card-roofingServicesCategory-cropped-20261026.webp`,
    alt: 'Real TNT Roofing project photo: completed roof replacement on a Northern Kentucky ranch home',
  },
  commercialRoofingCategory: {
    image: `${M}1e91c7d02_tnt-city-card-commercialRoofingCategory-cropped-20261026.webp`,
    alt: 'Real TNT Roofing project photo: crew working on a commercial roof replacement with exposed plywood sheathing',
  },
  gutterServicesCategory: {
    image: `${M}a5f2e35b7_tnt-city-card-gutterServicesCategory-clean-crop-20261026.webp`,
    alt: 'Real TNT Roofing project photo: new 6-inch black seamless gutters in Fort Thomas, KY',
  },
  sidingServicesCategory: {
    image: `${M}972a9715f_8bf3136ee_8596dfa6f_aftersidingpic-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed vinyl siding installation on a Northern Kentucky home',
  },

  // ── ResidentialCommercialSection ──
  residentialRoofing: {
    image: `${M}69ad592f8_355311807_IMG_9403-opt.webp`,
    alt: 'Real TNT Roofing project: completed residential shingle roof replacement with multi-tonal brown asphalt shingles',
  },
  commercialRoofingSection: {
    image: `${M}e62deaa9b_e8319dc6e_IMG_2543-opt.webp`,
    alt: 'Real TNT Roofing project photo: commercial flat roof replacement in Alexandria, KY',
  },

  // ── StormDamageRestoration benefits ──
  emergencyResponse: {
    image: `${M}5d3f050f9_aac6be01a_IMG_2523-opt.webp`,
    alt: 'Real TNT Roofing project photo: emergency blue tarp installed over storm-damaged shingles in Independence, KY',
  },
  stormRestoration: {
    image: `${M}ba4be9fee_41f82c26f_IMG_2432-opt.webp`,
    alt: 'Real TNT Roofing project photo: completed roof replacement following storm damage in Florence, KY',
  },
};